/**
 * Primary navigation — WCAG 2.1 AA compliant.
 *
 * Desktop: dropdowns are shown on CSS hover; JS adds aria-expanded for
 *          keyboard users and manages outside-click dismissal.
 *
 * Mobile:  slide-in drawer with inert/aria-hidden focus trap, accordion
 *          sub-menus with smooth height animation, body scroll-lock.
 *
 * Transparent header: adds .flip-header--scrolled once page scrolls past
 *          SCROLL_SOLID_PX so the header fades from transparent to white.
 */

const MOBILE_MQ      = window.matchMedia('(max-width: 1023.98px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// ── Entry point ───────────────────────────────────────────────────────────────

export function initNavigation() {
  initDrawer();
  initAccordion();
  initDropdowns();
  initSearch();
}

// ── Mobile drawer ─────────────────────────────────────────────────────────────

function initDrawer() {
  const header  = document.getElementById('flip-header');
  const toggle  = document.querySelector('.flip-nav__toggle');
  const close   = document.querySelector('.flip-nav__close');
  const drawer  = document.getElementById('flip-nav-drawer');
  const overlay = document.getElementById('flip-nav-overlay');

  if (!drawer) return;

  function setOpen(open) {
    drawer.classList.toggle('is-open', open);
    overlay?.classList.toggle('is-open', open && MOBILE_MQ.matches);

    if (toggle) {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute(
        'aria-label',
        open
          ? (toggle.dataset.labelClose || 'Close navigation menu')
          : (toggle.dataset.labelOpen  || 'Open navigation menu'),
      );
    }

    drawer.inert = !open;
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('nav-open', open);

    if (open) {
      requestAnimationFrame(() => drawer.querySelector('a, button')?.focus());
    } else {
      toggle?.focus();
    }
  }

  // Initialise closed
  setOpen(false);

  toggle?.addEventListener('click', () => setOpen(!drawer.classList.contains('is-open')));
  close?.addEventListener('click',  () => setOpen(false));
  overlay?.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      setOpen(false);
    }
  });

  MOBILE_MQ.addEventListener('change', () => {
    setOpen(false);
    closeAllDropdowns();
  });
}

// ── Mobile accordion ──────────────────────────────────────────────────────────

function initAccordion() {
  // ── Level-1 toggles (top-level accordion items) ───────────────────────
  document.querySelectorAll('.flip-nav__accordion-toggle').forEach((btn) => {
    const item = btn.closest('.flip-nav__accordion-item');
    const sub  = item?.querySelector('.flip-nav__accordion-sub');
    if (!item || !sub) return;

    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    sub.style.height = isOpen ? 'auto' : '0';

    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';

      // Collapse all siblings first
      item.closest('.flip-nav__accordion')
        ?.querySelectorAll('.flip-nav__accordion-toggle')
        .forEach((sibling) => {
          if (sibling === btn) return;
          sibling.setAttribute('aria-expanded', 'false');
          const sibSub = sibling.closest('.flip-nav__accordion-item')
            ?.querySelector('.flip-nav__accordion-sub');
          if (sibSub) collapse(sibSub);
        });

      btn.setAttribute('aria-expanded', String(open));
      open ? expand(sub) : collapse(sub);
    });
  });

  // ── Level-2 toggles (sub-items with level-3 children) ─────────────────
  document.querySelectorAll('.flip-nav__accordion-subtoggle').forEach((btn) => {
    const item = btn.closest('.flip-nav__accordion-subitem');
    const sub  = item?.querySelector('.flip-nav__accordion-sub');
    if (!item || !sub) return;

    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    sub.style.height = isOpen ? 'auto' : '0';

    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      open ? expand(sub) : collapse(sub);
    });
  });
}

function expand(el) {
  if (REDUCED_MOTION.matches) {
    el.style.height = 'auto';
    return;
  }
  el.style.height = el.scrollHeight + 'px';
  el.addEventListener('transitionend', () => { el.style.height = 'auto'; }, { once: true });
}

function collapse(el) {
  // Reset any open level-3 subs so they start closed next time
  el.querySelectorAll('.flip-nav__accordion-subtoggle[aria-expanded="true"]').forEach((btn) => {
    btn.setAttribute('aria-expanded', 'false');
    const sub = btn.closest('.flip-nav__accordion-subitem')?.querySelector('.flip-nav__accordion-sub');
    if (sub) sub.style.height = '0';
  });

  if (REDUCED_MOTION.matches) {
    el.style.height = '0';
    return;
  }
  // Pin to current pixel height so CSS transition can animate from it
  el.style.height = el.scrollHeight + 'px';
  // Force reflow before setting target height
  el.getBoundingClientRect();
  el.style.height = '0';
}

// ── Search panel ──────────────────────────────────────────────────────────────

function initSearch() {
  const panel    = document.getElementById('flip-search-panel');
  const input    = panel?.querySelector('.flip-search__input');
  const closeBtn = panel?.querySelector('.flip-search__close');
  const openBtns = document.querySelectorAll('[aria-controls="flip-search-panel"]');

  if (!panel) return;

  let lastOpener = null;

  function setOpen(open) {
    panel.classList.toggle('is-open', open);
    panel.inert = !open;
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('search-open', open);

    openBtns.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));

    if (open) {
      requestAnimationFrame(() => input?.focus());
    } else if (lastOpener) {
      lastOpener.focus();
      lastOpener = null;
    }
  }

  openBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      if (open) lastOpener = btn;
      setOpen(open);
    });
  });

  closeBtn?.addEventListener('click', () => setOpen(false));

  panel.addEventListener('click', (e) => {
    if (e.target === panel) setOpen(false);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) {
      setOpen(false);
    }
  });
}

// ── Desktop dropdowns ──────────────────────────────────────────────────────────

function initDropdowns() {
  const toggles = document.querySelectorAll('.flip-nav__dropdown-toggle');

  toggles.forEach((btn) => {
    const item    = btn.closest('.flip-nav__item');
    const submenu = item?.querySelector('.flip-nav__submenu');
    if (!item || !submenu) return;

    // Keyboard toggle (hover is handled by CSS)
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      closeAllDropdowns(btn);
      btn.setAttribute('aria-expanded', String(open));
    });

    // Close when focus moves out of the item
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) {
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  });

  // Close all dropdowns on outside click
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.flip-nav__item--has-children')) {
      closeAllDropdowns();
    }
  });

  // Escape closes open dropdown and returns focus to its toggle
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openToggle = document.querySelector('.flip-nav__dropdown-toggle[aria-expanded="true"]');
    if (openToggle) {
      closeAllDropdowns();
      openToggle.focus();
    }
  });
}

function closeAllDropdowns(except = null) {
  document.querySelectorAll('.flip-nav__dropdown-toggle[aria-expanded="true"]').forEach((btn) => {
    if (btn !== except) btn.setAttribute('aria-expanded', 'false');
  });
}

